import { Component, createRef } from 'react';
import fetch from 'isomorphic-unfetch';
import { withRouter } from 'next/router';
import { withTranslation } from 'next-i18next'; // https://www.i18next.com/
import mapElasticJobPostings from 'lib/mapElasticJobPostings';
import { getCssPropertyInt } from 'lib/getCssPropertyInt';
import setJobPostingsParams from 'lib/setJobPostingsParams';
import Pagination from './Pagination';
import JobPostingList from './JobPostingList';

class JobPostingOverview extends Component {
  fetchTimeout;

  constructor(props) {
    super(props);
    const { totalResults, serverSideResults } = this.props;
    this.state = {
      loading: false,
      resultCount: totalResults,
      paginatedJobPostings: [serverSideResults],
      currentPage: 1,
    };
    this.headingRef = createRef();
  }

  componentDidUpdate({ router: { query: prevQuery } }) {
    const {
      router: { query },
    } = this.props;
    if (query === prevQuery || query.view !== prevQuery.view) return;

    if (typeof window !== 'undefined') {
      if (this.fetchTimeout) window.clearTimeout(this.fetchTimeout);
      this.fetchTimeout = setTimeout(() => {
        this.getJobPostings();
      }, 300);
    } else {
      this.getJobPostings();
    }
  }

  scrollToHeading = () => {
    if (this.headingRef && typeof window !== 'undefined') {
      const searchCard = document.querySelector('.c-search-card');
      const header = document.querySelector('.c-header');
      const headingScrollOffset = getCssPropertyInt(
        this.headingRef.current,
        '--heading-scroll-offset'
      );
      const headingScrollMultiply = getCssPropertyInt(
        this.headingRef.current,
        '--heading-scroll-multiply'
      );

      const scrollOffset =
        searchCard.offsetHeight +
        header.offsetHeight +
        this.headingRef.current.offsetHeight * headingScrollMultiply +
        headingScrollOffset;
      window.scrollTo({ top: scrollOffset });
    }
  };

  getJobPostings = async (searchAfter = []) => {
    const {
      router: {
        query: { query, lat, lng },
      },
    } = this.props;
    const { paginatedJobPostings, currentPage } = this.state;
    // start loading
    this.setState({
      loading: true,
    });
    // get job postings
    const params = setJobPostingsParams({ query, lat, lng, searchAfter });
    const res = await fetch(`/api/elastic/job-postings?${params}`);
    const { totalResults, results: jobPostings } = await res.json();

    this.setState(
      {
        loading: false,
        resultCount: totalResults || 0,
        // if searchAfter is provided, we're paginating. Otherwise, simply overwrite the current results
        paginatedJobPostings: searchAfter.length
          ? [...paginatedJobPostings, jobPostings]
          : [jobPostings],
        currentPage: searchAfter.length ? currentPage + 1 : 1,
      },
      () => this.scrollToHeading()
    );
  };

  getPrevPage = () => {
    const { currentPage } = this.state;
    this.setState(
      {
        currentPage: currentPage - 1,
      },
      () => this.scrollToHeading()
    );
  };

  getNextPage = () => {
    const { currentPage, paginatedJobPostings } = this.state;

    if (paginatedJobPostings.length > currentPage) {
      this.setState(
        {
          currentPage: currentPage + 1,
        },
        () => this.scrollToHeading()
      );
      return;
    }

    const jobPostingsOnCurrentPage = paginatedJobPostings[currentPage - 1];
    const { sort: searchAfter } =
      jobPostingsOnCurrentPage[jobPostingsOnCurrentPage.length - 1];
    this.getJobPostings(searchAfter);
  };

  render() {
    const {
      t,
      splitView,
      router: {
        query: { query },
      },
    } = this.props;
    const { loading, resultCount, paginatedJobPostings, currentPage } =
      this.state;

    const totalPages = Math.ceil(
      resultCount / parseInt(process.env.NEXT_PUBLIC_PAGINATION_ITEMS, 10)
    );

    const headingKey = resultCount
      ? `jobPostingOverview.heading${
          query && query.trim() ? 'Search' : 'Default'
        }`
      : 'jobPostingOverview.noResults.heading';

    return (
      <>
        <h1
          ref={this.headingRef}
          className={`c-job-posting-list__heading u-pending ${
            loading ? 'u-pending--is-pending' : ''
          }`.trim()}
          data-cy="h1"
        >
          {t(headingKey, {
            q: query && query.trim(),
            count: resultCount,
            context: loading ? '_loading' : '',
          })}
        </h1>
        {!resultCount && (
          <p
            data-cy="no-results"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t('jobPostingOverview.noResults.text', {
                interpolation: { escapeValue: false },
              }),
            }}
          />
        )}

        <JobPostingList
          jobPostings={mapElasticJobPostings(
            paginatedJobPostings[currentPage - 1]
          )}
          loading={loading}
          currentPage={currentPage}
          cta={t('jobPostingOverview.viewJobPosting')}
          test="vacancies-list"
          splitView={splitView}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={this.getPrevPage}
          onNext={this.getNextPage}
          loading={loading}
          key={`Pagination_${new Date().toISOString()}`}
        />
      </>
    );
  }
}

export default withRouter(withTranslation()(JobPostingOverview));
